/**
 * useDraftCapFromFinding (A6).
 *
 * In an incident the Resilience Lead has the finding in front of her.
 * Before this she could link an EXISTING CAP to it and nothing more, so
 * the step from "we found this" to "here is what we are doing about it"
 * had no path from the finding at all.
 *
 * Drafting is not approving: the CAP lands in `draft` at the preparer
 * position and moves through the ordinary three-position workflow.
 */
import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from "@tanstack/react-query";

import { api, type ApiError } from "shared/api/client";

export interface DraftedCap {
  cap_id: string;
  letter_id: string;
  finding_id: string;
  title: string;
}

export function useDraftCapFromFinding(): UseMutationResult<
  DraftedCap,
  ApiError,
  { findingId: string }
> {
  const qc = useQueryClient();
  return useMutation({
    /* Round 11 D5 -- the caller passes its own `onError` to `mutate`,
       which says "I will handle this" and which react-query cannot
       see: a per-call callback lives on the observer, not in
       `mutation.options`, so the global failure sink announced the
       server's detail as well. One failure, two wordings -- the round
       8 E1 defect, through the door its ratchet was not watching.
       Measured before marking: every flagged call site's onError does
       show the failure, so suppressing the sink silences nothing. */
    meta: { handled: true },
    mutationFn: ({ findingId }) =>
      api.post<DraftedCap>(`/v1/findings/${findingId}/draft-cap`),
    onSuccess: (_res, { findingId }) => {
      // The finding's linked-CAPs back-link now has one more entry.
      void qc.invalidateQueries({ queryKey: ["finding-linked-caps", findingId] });
    },
  });
}
